/**
 * 🎯 DASHBOARD PAGE COMPONENT
 * ════════════════════════════════════════════════════════════════════════
 * 
 * RESPONSABILIDADES:
 * 1. Fetch dashboard por ID
 * 2. Mostrar header con metadata
 * 3. Renderizar grid editable con widgets
 * 4. Mostrar loading/error states
 * 
 * MODO ÚNICO:
 * - Todos los dashboards son editables
 * - Drag & resize siempre habilitado
 * - Sin distinción entre mock y real
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, RotateCcw } from 'lucide-react';
import './DashboardPage.css';
import { EditorialDashboardGrid } from '../domains/dashboards/components/EditorialDashboardGrid';
import type { Dashboard, DashboardWidget } from '../types/dashboard';
import { getDashboard, updateDashboard } from '../domains/dashboards/services/dashboards.service';
import type { Dashboard as ApiDashboard } from '../domains/dashboards/services/dashboards.service';
import { MOCK_DATASOURCE } from './mock-data/datasource.mock';
import { generateMockDashboard } from './mock-data/dashboard-generator.mock';
import ManualChartBuilder from '../shared/ui/ManualChartBuilder';
import ConfirmDialog from '../shared/ui/ConfirmDialog';
import api from '../shared/services/apiClient';
import toast from 'react-hot-toast';

interface DashboardPageProps {
  dashboardId?: string;
  onBack?: () => void;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ dashboardId: propDashboardId, onBack }) => {
  const { id } = useParams<{ id: string }>();
  const dashboardId = propDashboardId || id;

  const normalizeDashboard = (data: ApiDashboard): Dashboard => {
    const resolvedWidgets =
      Array.isArray(data.widgets) ? data.widgets
        : Array.isArray((data as any).layout?.widgets) ? (data as any).layout.widgets
        : Array.isArray((data as any).layoutJson?.widgets) ? (data as any).layoutJson.widgets
        : [];

    return {
      id: data.id,
      title: (data as any).title ?? data.name ?? 'Dashboard',
      description: data.description ?? undefined,
      widgets: resolvedWidgets,
      dataSourceId: data.dataSourceId ?? undefined,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  };
  
  // Detectar si es el dashboard de desarrollo
  const isDevDashboard = dashboardId === 'mock-dashboard-dev';
  
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [initialDashboard, setInitialDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [rawDatasourceData, setRawDatasourceData] = useState<any[]>([]);
  
  // Generate mock dashboard (solo para desarrollo)
  const mockDashboard = useMemo(() => {
    if (!isDevDashboard) return null;
    return generateMockDashboard(MOCK_DATASOURCE);
  }, [isDevDashboard]);
  
  useEffect(() => {
    if (!dashboardId) {
      setError('No dashboard ID provided');
      setLoading(false);
      return;
    }
    
    // Dashboard de desarrollo: cargar datos mock
    if (isDevDashboard && mockDashboard) {
      setDashboard(mockDashboard);
      // Deep clone para evitar mutaciones
      setInitialDashboard(JSON.parse(JSON.stringify(mockDashboard)));
      setRawDatasourceData(MOCK_DATASOURCE.rawSample || []);
      setLoading(false);
      return;
    }
    
    // Dashboard real: fetch desde backend
    fetchDashboard(dashboardId);
  }, [dashboardId, isDevDashboard, mockDashboard]);
  
  // Bloquear scroll cuando modales estén abiertos
  useEffect(() => {
    if (showAddWidgetModal || showResetConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAddWidgetModal, showResetConfirm]);
  
  const fetchDashboard = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getDashboard(id);

      if (!data) {
        setDashboard(null);
        setError('Dashboard no encontrado');
        return;
      }

      const normalized = normalizeDashboard(data);
      setDashboard(normalized);
      // Deep clone para evitar mutaciones
      setInitialDashboard(JSON.parse(JSON.stringify(normalized)));
      setHasLocalChanges(false);
      
      // Cargar datos crudos del datasource para ManualChartBuilder
      const datasourceId = data.dataSourceId || (data as any).layout?.dataSourceId;
      if (datasourceId) {
        try {
          console.log('📊 [DashboardPage] Loading raw data from datasource:', datasourceId);
          const sql = `SELECT * FROM "${datasourceId}" LIMIT 1000`;
          const dsResponse = await api.post(`/datasources/${datasourceId}/sql`, { sql });
          const rawData = dsResponse.data?.data || [];
          console.log('✅ [DashboardPage] Raw data loaded:', rawData.length, 'records');
          if (rawData.length > 0) {
            console.log('📋 [DashboardPage] Available columns:', Object.keys(rawData[0]));
          }
          setRawDatasourceData(rawData);
        } catch (err) {
          console.error('❌ [DashboardPage] Error loading datasource data:', err);
          setRawDatasourceData([]);
        }
      }
      
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };
  
  // Función para agregar nuevo widget
  const handleAddWidget = async (widgetId: string, config: any) => {
    if (!dashboard) return;
    
    const datasourceId = dashboard.dataSourceId;
    
    // Crear nuevo widget
    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}`,
      title: config.title || 'Nuevo Widget',
      type: config.type || 'bar',
      data: [],
      config: config.config || {},
      layout: {
        x: 0,
        y: 0,
        w: 6,
        h: 300
      }
    };
    
    // Agregar al dashboard (estado local) - primero sin datos
    setDashboard(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        widgets: [...prev.widgets, newWidget]
      };
    });
    
    setHasLocalChanges(true);
    setShowAddWidgetModal(false);
    
    // Ejecutar SQL para obtener datos reales
    if (datasourceId && config.config?.sql) {
      try {
        console.log('🔄 [DashboardPage] Executing SQL for new widget...');
        
        // Reemplazar placeholder con datasourceId real
        let sql = config.config.sql;
        sql = sql.replace(/DATASOURCE_ID/g, datasourceId);
        
        console.log('📊 [DashboardPage] SQL:', sql);
        
        const response = await api.post(`/datasources/${datasourceId}/sql`, { sql });
        const data = response.data?.data || [];
        
        console.log('✅ [DashboardPage] Data loaded:', data.length, 'records');
        
        // Actualizar widget con datos reales
        setDashboard(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            widgets: prev.widgets.map(w => 
              w.id === newWidget.id ? { ...w, data } : w
            )
          };
        });
        
        toast.success('Widget agregado con datos reales');
      } catch (error) {
        console.error('❌ [DashboardPage] Error executing SQL:', error);
        toast.error('Widget agregado sin datos. Error ejecutando SQL.');
      }
    } else {
      toast.success('Widget agregado al dashboard');
    }
  };
  
  // Función para resetear dashboard
  const handleResetDashboard = async () => {
    if (!initialDashboard) return;
    
    try {
      console.log('🔄 [DashboardPage] Restableciendo dashboard al estado inicial');
      
      // Deep clone para restaurar sin mutar el estado inicial
      const restoredDashboard = JSON.parse(JSON.stringify(initialDashboard));
      setDashboard(restoredDashboard);
      
      // Persistir la restauración en el backend
      await api.put(`/dashboards/${restoredDashboard.id}`, {
        widgetsMode: 'replace',
        widgets: restoredDashboard.widgets.map((w: any) => ({
          id: w.id,
          type: w.type,
          title: w.title,
          data: w.data || [],
          config: w.config || {},
          layout: w.layout || { x: 0, y: 0, w: 6, h: 4 }
        }))
      });
      
      setHasLocalChanges(false);
      setShowResetConfirm(false);
      toast.success('Dashboard restablecido al estado inicial');
      console.log('✅ [DashboardPage] Dashboard restablecido y persistido en backend');
    } catch (error) {
      console.error('❌ [DashboardPage] Error restableciendo dashboard:', error);
      toast.error('Error restableciendo dashboard');
    }
  };
  
  // Función para eliminar widget
  const handleDeleteWidget = async (widgetId: string) => {
    if (!dashboard || !dashboard.dataSourceId) return;
    
    const prevDashboard = dashboard;
    
    try {
      console.log('🗑️ [DashboardPage] Eliminando widget:', widgetId);
      
      // Actualizar estado local inmediatamente (optimistic update)
      const updatedWidgets = dashboard.widgets.filter(w => w.id !== widgetId);
      setDashboard(prev => prev ? { ...prev, widgets: updatedWidgets } : prev);
      
      // Persistir en backend
      await api.put(`/dashboards/${dashboard.id}`, {
        widgetsMode: 'replace',
        widgets: updatedWidgets.map(w => ({
          id: w.id,
          type: w.type,
          title: w.title,
          data: w.data || [],
          config: w.config || {},
          layout: w.layout || { x: 0, y: 0, w: 6, h: 4 }
        }))
      });
      
      // ✅ NO actualizar initialDashboard para que "Restablecer" recupere el widget
      // Marcar que hay cambios locales
      setHasLocalChanges(true);
      
      toast.success('Widget eliminado correctamente');
      console.log('✅ [DashboardPage] Widget eliminado del backend');
    } catch (error) {
      console.error('❌ [DashboardPage] Error eliminando widget:', error);
      toast.error('Error eliminando widget');
      
      // Revertir el optimistic update en caso de error
      setDashboard(prevDashboard);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="dashboard-page-loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Cargando dashboard...</div>
      </div>
    );
  }
  
  // Error state
  if (error || !dashboard) {
    return (
      <div className="dashboard-page-error">
        <div className="error-icon">⚠️</div>
        <div className="error-title">Error al cargar dashboard</div>
        <div className="error-message">{error || 'Dashboard no encontrado'}</div>
        <button 
          className="error-retry-button"
          onClick={() => dashboardId && fetchDashboard(dashboardId)}
        >
          Reintentar
        </button>
      </div>
    );
  }
  
  // Success state
  return (
    <div className="dashboard-page">
      
      {/* Dev dashboard banner */}
      {isDevDashboard && (
        <div className="bg-gradient-to-r from-sky-900/40 to-indigo-900/40 border border-sky-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎨</span>
            <div className="flex-1">
              <h3 className="text-white font-semibold text-sm">DASHBOARD DE DESARROLLO</h3>
              <p className="text-slate-300 text-xs mt-0.5">Mock data para testing | Editable | Drag & resize activado</p>
            </div>
            <div className="px-3 py-1.5 bg-sky-500/20 border border-sky-500/40 rounded-md">
              <span className="text-sky-300 text-xs font-mono">DEV</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div>
            <h1 className="dashboard-title">
              {dashboard.title || 'Dashboard'}
            </h1>
            
            {dashboard.description && (
              <p className="dashboard-description">
                {dashboard.description}
              </p>
            )}
          </div>
          
          {/* Botones de acción (solo en modo editable) */}
          {/* TEMPORALMENTE OCULTOS PARA PRODUCCIÓN */}
          {false && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAddWidgetModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors shadow-lg shadow-sky-500/20"
              >
                <Plus className="w-4 h-4" />
                <span>Añadir widget</span>
              </button>
              
              <button
                onClick={() => setShowResetConfirm(true)}
                disabled={!hasLocalChanges}
                title={!hasLocalChanges ? 'No hay cambios para restablecer' : 'Restablecer dashboard al estado inicial'}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/20"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Restablecer dashboard</span>
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Grid: Siempre editable */}
      <EditorialDashboardGrid 
        dashboard={dashboard} 
        editable={true}
        onLayoutChange={(widgets) => {
          // Detectar cambios en layout (drag/resize)
          setHasLocalChanges(true);
        }}
        onWidgetConfigSave={async (widgetId, config) => {
          const nextTitle = typeof config.title === 'string' && config.title.trim()
            ? config.title.trim()
            : dashboard.widgets.find((widget) => widget.id === widgetId)?.title;

          // Actualizar estado local (sin llamar al backend)
          setDashboard((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              widgets: prev.widgets.map((widget) =>
                widget.id === widgetId
                  ? {
                      ...widget,
                      title: nextTitle || widget.title,
                      config: { ...config }
                    }
                  : widget
              )
            };
          });
          
          setHasLocalChanges(true);
          toast.success('Widget actualizado. Los cambios son locales.');
        }}
        onWidgetDelete={handleDeleteWidget}
      />
      
      {/* Modal: Añadir widget */}
      {showAddWidgetModal && (
        <ManualChartBuilder
          isOpen={showAddWidgetModal}
          onClose={() => setShowAddWidgetModal(false)}
          widget={null}
          datasourceData={rawDatasourceData}
          onSave={handleAddWidget}
        />
      )}
      
      {/* Modal: Confirmación de reset */}
      <ConfirmDialog
        isOpen={showResetConfirm}
        title="Restablecer dashboard"
        message="¿Estás seguro que deseas restablecer el dashboard? Los cambios que hayas hecho no se guardarán."
        confirmText="Restablecer"
        cancelText="Cancelar"
        onConfirm={handleResetDashboard}
        onCancel={() => setShowResetConfirm(false)}
        type="danger"
      />
      
    </div>
  );
};

export default DashboardPage;
